import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealLines, Reveal } from "./motion";
import { GhlForm } from "./GhlForm";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBlob1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const yBlob2 = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 25]);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24"
      data-testid="hero-section"
    >
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-12 px-5 md:px-10 lg:grid-cols-12 lg:gap-10">
        {/* Left — copy */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mb-6 inline-flex items-center gap-2 border-2 border-ink px-3 py-1.5 font-mono text-xs font-bold uppercase tracking-[0.22em] text-ink"
            data-testid="hero-eyebrow"
          >
            <span className="h-2 w-2 bg-acid" /> Intentional Networking Edition
          </motion.p>

          <h1 className="font-display text-[2.5rem] font-black uppercase leading-[0.94] tracking-tighter text-ink sm:text-6xl lg:text-[4.4rem]">
            <RevealLines
              testid="hero-headline"
              delay={0.15}
              lines={["The Dope", "Conversation", "Starter"]}
            />
          </h1>

          <Reveal delay={0.7} className="mt-8 max-w-xl">
            <p className="text-lg font-medium leading-relaxed text-ink/80 md:text-xl" data-testid="hero-subhead">
              Networking is not about collecting contacts. It is about creating{" "}
              <span className="bg-cyan/30 px-1 font-bold text-ink">connections that have somewhere to go.</span>
            </p>
          </Reveal>

          <Reveal delay={0.82} className="mt-6 max-w-xl">
            <p className="border-l-4 border-acid pl-5 text-base leading-relaxed text-ink/70 md:text-lg" data-testid="hero-body">
              Five steps for an intentional conversation. No script. No pitch. No elevator pitch.
              Use this before your next event.
            </p>
          </Reveal>
        </div>

        {/* Right — form */}
        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: yBlob1 }}
            className="pointer-events-none absolute -right-6 -top-10 h-40 w-40 rounded-full bg-cyan/70 blur-[2px] md:h-52 md:w-52"
            aria-hidden
          />
          <motion.div
            style={{ y: yBlob2 }}
            className="pointer-events-none absolute -bottom-8 -left-8 h-36 w-36 rounded-full bg-acid/70 md:h-48 md:w-48"
            aria-hidden
          />
          <motion.div
            style={{ rotate }}
            className="pointer-events-none absolute -left-4 top-10 h-16 w-16 border-4 border-ink bg-ink md:h-20 md:w-20"
            aria-hidden
          />

          <Reveal delay={0.4}>
            <div
              id="lead-form-anchor"
              className="relative z-10 border-4 border-ink bg-white p-6 shadow-[10px_10px_0px_0px_#0A1128] md:p-8"
              data-testid="hero-form-card"
            >
              <p className="mb-5 font-display text-xl font-extrabold uppercase tracking-tight text-ink">
                Send me The Dope Conversation Starter
              </p>
              <GhlForm />
              <p className="mt-5 border-t-2 border-solid border-ink/20 pt-4 text-sm leading-relaxed text-ink/60" data-testid="hero-microcopy">
                Free. No card. The Intentional Networking Edition, straight to your inbox. Use it before your next event.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
